import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Trash2, Edit2, Megaphone, ArrowLeft, Calendar, CheckCircle2, Clock, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = { message: '', active: true, starts_at: '', ends_at: '' };

// Convert a local <input type="datetime-local"> value to an ISO string (UTC)
const localToIso = (value) => {
  if (!value) return '';
  // "YYYY-MM-DDTHH:mm" interpreted as local time
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toISOString();
};

// Convert an ISO string to a "YYYY-MM-DDTHH:mm" local-time value for the input
const isoToLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const prettyDt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusOf = (a) => {
  if (!a.active) return { label: 'Disabled', tone: 'bg-gray-200 text-gray-700', Icon: XCircle };
  const now = new Date();
  const start = a.starts_at ? new Date(a.starts_at) : null;
  const end = a.ends_at ? new Date(a.ends_at) : null;
  if (start && now < start) return { label: 'Scheduled', tone: 'bg-blue-100 text-blue-800', Icon: Clock };
  if (end && now > end) return { label: 'Expired', tone: 'bg-yellow-100 text-yellow-800', Icon: Clock };
  return { label: 'Live', tone: 'bg-green-100 text-green-800', Icon: CheckCircle2 };
};

export default function AdminAnnouncements() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const admin = localStorage.getItem('adminEmail');
    if (!admin) {
      navigate('/admin/login');
      return;
    }
    fetchItems();
  }, [navigate]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/announcements`);
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast({ title: 'Failed to load announcements', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (a) => {
    setForm({
      message: a.message || '',
      active: !!a.active,
      starts_at: isoToLocal(a.starts_at),
      ends_at: isoToLocal(a.ends_at),
    });
    setEditingId(a.id);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const save = async () => {
    if (!form.message.trim()) {
      toast({ title: 'Message is required', variant: 'destructive' });
      return;
    }
    if (form.starts_at && form.ends_at) {
      const s = new Date(form.starts_at);
      const e = new Date(form.ends_at);
      if (!isNaN(s.getTime()) && !isNaN(e.getTime()) && e <= s) {
        toast({ title: 'End time must be after start time', variant: 'destructive' });
        return;
      }
    }
    try {
      setSaving(true);
      const payload = {
        message: form.message.trim(),
        active: !!form.active,
        starts_at: localToIso(form.starts_at),
        ends_at: localToIso(form.ends_at),
      };
      if (editingId) {
        await axios.patch(`${API}/admin/announcements/${editingId}`, payload);
        toast({ title: 'Announcement updated' });
      } else {
        await axios.post(`${API}/admin/announcements`, payload);
        toast({ title: 'Announcement created' });
      }
      closeForm();
      fetchItems();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/announcements/${deleteTarget.id}`);
      toast({ title: 'Announcement deleted' });
      setDeleteTarget(null);
      fetchItems();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const toggleActive = async (a) => {
    try {
      await axios.patch(`${API}/admin/announcements/${a.id}`, { active: !a.active });
      fetchItems();
    } catch (e) {
      toast({ title: 'Update failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-[#1e3a5f] text-white py-6">
        <div className="container mx-auto px-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-white hover:bg-white/10"
              data-testid="back-to-admin"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
              <Megaphone className="w-6 h-6" /> Landing Page Announcements
            </h1>
          </div>
          <Button
            onClick={openNew}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="admin-announcement-new"
          >
            <Plus className="w-4 h-4 mr-2" /> New Announcement
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card className="p-5 mb-6 bg-amber-50 border-amber-200">
          <p className="text-sm text-amber-900">
            <strong>How it works:</strong> The hero logo on the landing page flips every 5 seconds to show your announcement.
            Only one <em>Live</em> announcement is shown at a time (newest first). Use the start/end times to schedule promos,
            maintenance notices, or special offers. Leave both blank to show immediately with no expiry.
          </p>
        </Card>

        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading announcements...</Card>
        ) : items.length === 0 ? (
          <Card className="p-10 text-center">
            <Megaphone className="w-14 h-14 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-700 font-semibold">No announcements yet</p>
            <p className="text-sm text-gray-500 mt-1">Create one to show it on the landing page.</p>
            <Button onClick={openNew} className="mt-4 bg-red-600 hover:bg-red-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> Create First Announcement
            </Button>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="admin-announcement-list">
            {items.map((a) => {
              const st = statusOf(a);
              const StIcon = st.Icon;
              return (
                <Card key={a.id} className="p-4 md:p-5" data-testid={`announcement-row-${a.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${st.tone}`}>
                          <StIcon className="w-3.5 h-3.5" /> {st.label}
                        </span>
                        {!a.active && (
                          <span className="text-xs text-gray-500">(toggled off)</span>
                        )}
                      </div>
                      <p className="text-[15px] md:text-base text-gray-900 whitespace-pre-wrap break-words font-medium">
                        {a.message}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Start: {prettyDt(a.starts_at)}</span>
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> End: {prettyDt(a.ends_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleActive(a)}
                        data-testid={`announcement-toggle-${a.id}`}
                      >
                        {a.active ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEdit(a)}
                        data-testid={`announcement-edit-${a.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setDeleteTarget(a)}
                        data-testid={`announcement-delete-${a.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Form modal */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closeForm(); }}
          data-testid="announcement-form-overlay"
        >
          <Card className="w-full max-w-lg bg-white max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b bg-red-600 text-white rounded-t-lg">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Megaphone className="w-5 h-5" />
                {editingId ? 'Edit Announcement' : 'New Announcement'}
              </h2>
              <p className="text-xs text-red-50 mt-1">Shown on the hero logo flip card</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label htmlFor="ann-message">Message *</Label>
                <Textarea
                  id="ann-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="e.g. ⚡ Spring promo: free load posts all week!"
                  rows={4}
                  maxLength={240}
                  className="mt-1"
                  data-testid="announcement-message-input"
                />
                <p className="text-xs text-gray-500 mt-1">{form.message.length}/240 characters · Line breaks are preserved</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ann-start">Start (optional)</Label>
                  <Input
                    id="ann-start"
                    type="datetime-local"
                    value={form.starts_at}
                    onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                    className="mt-1"
                    data-testid="announcement-start-input"
                  />
                </div>
                <div>
                  <Label htmlFor="ann-end">End (optional)</Label>
                  <Input
                    id="ann-end"
                    type="datetime-local"
                    value={form.ends_at}
                    onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                    className="mt-1"
                    data-testid="announcement-end-input"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="w-4 h-4 accent-red-600"
                  data-testid="announcement-active-toggle"
                />
                <span className="text-sm text-gray-700"><strong>Active</strong> — show on the landing page when inside the date window</span>
              </label>
            </div>
            <div className="p-5 border-t flex justify-end gap-2 bg-gray-50 rounded-b-lg">
              <Button variant="outline" onClick={closeForm} disabled={saving} data-testid="announcement-cancel">
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="announcement-save"
              >
                {saving ? 'Saving...' : (editingId ? 'Update' : 'Create')}
              </Button>
            </div>
          </Card>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this announcement?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. The announcement will no longer be available in the CMS.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmDelete}
              data-testid="announcement-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
